import type { OverlaySettings } from "./settings";
import { formatLocal, parseLocal } from "./time";
import type { FieldId, GameState, ScheduledGame, Scene } from "./types";

/**
 * The auto scene: which of live/break the overlay should be showing, derived
 * from facts that already exist instead of a stored value a scorekeeper must
 * remember to keep true. The failure this replaces was structural, not a
 * training problem: scene was independent state, so "schedule on stream
 * through a whole game" and "scorebug over an empty pitch" were both one
 * forgotten tap away.
 *
 * The rules, in priority order:
 *
 *   1. A manual pin (live/break/hidden) is obeyed verbatim. Auto never
 *      produces "hidden" - that one exists for OBS setup and stoppages only.
 *   2. Play outranks the calendar: h1/h2/half render the bug (the "live"
 *      scene's own takeover logic picks the halftime card), whatever the
 *      schedule thinks should be happening. A running game is never yanked
 *      because the next slot's window opened.
 *   3. Final holds the "live" scene (whose takeover is the final card) for
 *      `finalHoldMs` past `finalAt` - the organiser's `finalCardSec`, giving
 *      way when the next game on this field is due sooner than that - then
 *      falls to the schedule. A final without a stamp - a state persisted
 *      before `finalAt` existed - counts as expired rather than holding the
 *      card forever.
 *   4. Pregame shows the intro card only once the keeper has configured the
 *      game AND the start is within `pregameLeadMin` (a configured game with
 *      no schedule row - a friendly - shows it unconditionally: configuring
 *      it was the intent). Otherwise, and on an untouched field, the
 *      schedule.
 *
 * Pure and clock-injected so the showcase can pin any moment and bun can test
 * the windows. `now` is SERVER time (client clock + skew); `nextStartAtMs` is
 * the start that matters for the period - the configured game's own in
 * pregame, the NEXT game's after a final (`nextStartAfter`) - parsed by the
 * caller. Schedule times are zone-less local strings, and
 * `new Date("2026-09-05T09:00")` reads them in the machine's own zone, which
 * is correct precisely because the OBS machine stands at the venue (the break
 * card's day picker leans on the same fact).
 */
export function resolveScene(
  state: Pick<GameState, "scene" | "period" | "configured" | "finalAt">,
  /** Pregame: start of the CONFIGURED game. Final: start of the NEXT game on
   *  this field. Null: no schedule row / not looked up / no next game. */
  nextStartAtMs: number | null,
  /** Server-skewed epoch ms. */
  now: number,
  overlay: OverlaySettings,
): Exclude<Scene, "auto"> {
  if (state.scene !== "auto") return state.scene;

  if (state.period === "h1" || state.period === "h2" || state.period === "half") return "live";

  if (state.period === "final") {
    const at = state.finalAt;
    if (at === undefined) return "break";
    return now - at < finalHoldMs(overlay, at, nextStartAtMs) ? "live" : "break";
  }

  // pregame
  if (state.configured !== true) return "break";
  if (nextStartAtMs === null) return "live";
  return now >= nextStartAtMs - overlay.pregameLeadMin * 60_000 ? "live" : "break";
}

/**
 * The least the intro card gets before the next pull when it and the final
 * card have to share a short gap: enough to glance at two rosters.
 */
export const INTRO_MIN_MS = 20_000;

/**
 * The least the final card keeps whatever the schedule says. The board is the
 * only place the result is shown at all, and a result that never reached the
 * stream is worse than an intro that ran short.
 */
export const FINAL_MIN_MS = 15_000;

/**
 * How long the final card holds THIS time, in milliseconds.
 *
 * `finalCardSec` is the organiser's number for a day that runs to plan. But
 * ultimate is played to a score: a point is finished however far past the
 * slot the clock is, so a game that ends late is the ordinary case, and the
 * next game is then due at once. A fixed hold spent the whole gap on the
 * result and left the keeper to choose between cutting the card by hand and
 * never showing the rosters - the manual step this exists to remove. So the
 * hold gives way: it shrinks until the intro card has `INTRO_MIN_MS` before
 * the next start, and stops shrinking at `FINAL_MIN_MS`. A hold the organiser
 * already set below that floor (0 is "no final card") is a choice, and kept.
 *
 * `nextStartAtMs` is the next unplayed game on THIS field (`nextStartAfter`).
 * Null - no schedule, no next game, or the next one is tomorrow - keeps the
 * full number. A start already in the past is the overrun itself and squeezes
 * the hold to the floor: the pull comes the moment the teams are lined up.
 */
export function finalHoldMs(
  overlay: OverlaySettings,
  finalAt: number,
  nextStartAtMs: number | null,
): number {
  const full = overlay.finalCardSec * 1000;
  if (nextStartAtMs === null) return full;
  const room = nextStartAtMs - finalAt - INTRO_MIN_MS;
  return Math.max(Math.min(full, FINAL_MIN_MS), Math.min(full, room));
}

/**
 * When the next game on this field is due, as epoch ms, or null.
 *
 * The earliest unplayed row on the field other than the one that just
 * finished - excluded by ID, because its own result lands a moment AFTER the
 * final and for that moment it still looks unplayed - on the same local day
 * as the final: tomorrow's 09:00 is no reason to cut tonight's last result
 * short. Same-day rows already in the past count; that IS the overrun case
 * this exists for, and the earliest of them is also the row the break card
 * marks NEXT, so the two never disagree about which game is coming.
 *
 * Zone-less local times, read in this machine's zone like everywhere on the
 * overlay: the OBS machine stands at the venue.
 */
export function nextStartAfter(
  schedule: readonly Pick<ScheduledGame, "id" | "fieldId" | "startAt" | "result">[],
  fieldId: FieldId,
  finishedId: string | null,
  finalAt: number,
): number | null {
  const day = formatLocal(new Date(finalAt)).slice(0, 10);
  let best: number | null = null;
  for (const g of schedule) {
    if (g.fieldId !== fieldId || g.id === finishedId || g.result !== undefined) continue;
    const d = parseLocal(g.startAt);
    if (d === null || formatLocal(d).slice(0, 10) !== day) continue;
    const t = d.getTime();
    if (best === null || t < best) best = t;
  }
  return best;
}

/**
 * Does the PREVIOUS game's final card still own the frame?
 *
 * The card's window (`finalCardSec` past `finalAt`) is remembered by the
 * overlay past the state that carried it, because "Set up game" replaces that
 * state with the next matchup's pregame twenty seconds after the horn. Until
 * this existed the memory yielded to the next game's intro card, and on a day
 * running late that produced the worst sequence the stinger can play: FINAL
 * for twenty seconds, wipe, rosters for two, wipe, score bug - the next game's
 * start had long passed, so its intro was due the instant the keeper loaded
 * it, and the pull followed a breath later. The result of the game people
 * just watched outranks the preview of the one about to start, for exactly
 * the window the organiser configured. Nothing outranks play (h1 ends the
 * hold at once), and a pin is still obeyed verbatim.
 *
 * `heldUntil` is `finalAt + finalHoldMs(...)`, remembered by the caller; null
 * when no final has been seen.
 */
/**
 * Does this field belong on ANOTHER field's break card, in the ELSEWHERE strip?
 *
 * Decided from the same facts the field's own overlay derives its scene from,
 * not from the stored pin. The strip used to keep a row only when
 * `state.scene === "live"`, which was right while every configured game was
 * pinned live - and wrong from the day auto became the default, because auto
 * IS the stored value for every normal game, so the strip has been empty on
 * every break card since unless a keeper pinned something by hand.
 *
 * What counts as action worth a row: a half being played, the break between
 * them (the score is live and the game is coming back), and a result for as
 * long as that field's own final card holds. A game nobody has started is not
 * action - this field's break card already lists its own schedule, and a 0-0
 * row cannot say whether the pull is in five minutes or five seconds ago - so
 * auto drops pregame however configured it is. The pins keep their word:
 * "live" shows (a configured matchup even before the pull, as the pin means
 * "broadcast this"), "break" and "hidden" never do, and an untouched field
 * shows nothing whatever it is pinned to.
 *
 * `now` is server-skewed, like everything the resolver is given; the strip
 * evaluates this at poll time, so a result lingers at most one poll past its
 * window. The FULL window, not the squeezed one (`finalHoldMs`): the strip
 * does not know the other field's schedule, and a result staying a little
 * longer in a strip is harmless where a result cut short on its own board is
 * not.
 */
export function showsOnTicker(
  state: Pick<GameState, "scene" | "period" | "configured" | "finalAt">,
  now: number,
  overlay: OverlaySettings,
): boolean {
  if (state.scene === "break" || state.scene === "hidden") return false;
  if (state.period === "pregame") return state.scene === "live" && state.configured === true;
  if (state.period === "final") {
    if (state.scene === "live") return true;
    const at = state.finalAt;
    if (at === undefined) return false;
    return now - at < overlay.finalCardSec * 1000;
  }
  return true;
}

export function holdsFinal(
  state: Pick<GameState, "scene" | "period">,
  heldUntil: number | null,
  now: number,
): boolean {
  if (heldUntil === null) return false;
  if (state.scene !== "auto") return false;
  if (state.period !== "pregame") return false;
  return now < heldUntil;
}
